import {
  IconButton,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { DamsColors } from "../styles/DamsColors";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import React, { useState } from "react";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

interface IconStyles {
  leftIconSx: SxProps<Theme>;
  rightIconSx: SxProps<Theme>;
}

export interface AuthInputProps {
  placeholder?: string;
  label?: string;
  type?: "email" | "password" | "re-enter-password";
  iconStyles?: IconStyles;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string | number;
  error?: boolean;
  errorMessage?: string;
}

const AuthInput: React.FC<AuthInputProps> = ({
  label,
  placeholder,
  type = "email",
  iconStyles = {
    leftIconSx: styles.leftInputIcon,
    rightIconSx: styles.rightInputIcon,
  },
  onChange,
  value,
  error,
  errorMessage,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const Icons = IconGroup(type, showPassword);
  const { input_label, input_placeholder } = getLabelAndPlaceholder(type);

  const rightIcon =
    type === "password" ? (
      <IconButton onClick={handleTogglePasswordVisibility} sx={{ p: 0 }}>
        {Icons[1]?.icon &&
          React.cloneElement(Icons[1].icon, {
            sx: { ...iconStyles?.rightIconSx },
          })}
      </IconButton>
    ) : (
      Icons[1]?.icon &&
      React.cloneElement(Icons[1].icon, { sx: { ...iconStyles?.rightIconSx } })
    );

  return (
    <Stack height={"100%"} width={"100%"}>
      <Typography
        fontFamily={"Inter"}
        color={DamsColors.Authentication.InputLabel}
        fontSize={14}
        fontWeight={500}
        mb={1}
      >
        {label??input_label}
      </Typography>
      <TextField
        variant="standard"
        placeholder={placeholder??input_placeholder}
        onChange={onChange}
        value={value}
        type={type === "password" && !showPassword ? "password" : "text"}
        InputProps={{
          startAdornment:
            Icons[0]?.icon &&
            React.cloneElement(Icons[0].icon, {
              sx: { ...iconStyles?.leftIconSx },
            }),
          endAdornment: type === "re-enter-password" ? <></> : rightIcon,
        }}
        fullWidth
        sx={styles.authInput}
      />
        <Typography variant="caption" color={"red"} minHeight={20}>
          {error && errorMessage}
        </Typography>
    
    </Stack>
  );
};

function IconGroup(
  type: "email" | "password" | "re-enter-password",
  showPassword: boolean
) {
  if (type === "email") {
    return [
      { name: "email", icon: <MailOutlineRoundedIcon /> },
      { name: "help", icon: <HelpOutlineIcon /> },
    ];
  }

  if (type === "password") {
    return [
      { name: "lock", icon: <LockOutlinedIcon /> },
      {
        name: "visibility",
        icon: showPassword ? (
          <VisibilityOffOutlinedIcon />
        ) : (
          <VisibilityOutlinedIcon />
        ),
      },
    ];
  }

  if (type === "re-enter-password") {
    return [
      { name: "lock", icon: <LockOutlinedIcon /> },
      {
        name: "visibility",
        icon: showPassword ? (
          <VisibilityOffOutlinedIcon />
        ) : (
          <VisibilityOutlinedIcon />
        ),
      },
    ];
  }

  return [];
}

const getLabelAndPlaceholder = (
  type: "email" | "password" | "re-enter-password"
) => {
  switch (type) {
    case "email":
      return {
        input_label: "Email",
        input_placeholder: "Enter your email",
      };
    case "password":
      return {
        input_label: "Password",
        input_placeholder: "Enter your password",
      };
    case "re-enter-password":
      return {
        input_label: "Password",
        input_placeholder: "Repeat Password",
      };

    default:
      return {
        input_label: "Auth label",
        input_placeholder: "Auth Input Placeholder",
      };
  }
};

const styles = {
  authInput: {
    backgroundColor: "white",
    borderRadius: "8px",
    border: `1px solid ${DamsColors.Authentication.InputBorder}`,
    height: "48px",
    justifyContent: "center",
    color: DamsColors.Authentication.InputLabel,
    fontFamily: "Inter",
    px: "15px",

    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
  },

  leftInputIcon: {
    mr: 1,
    color: `${DamsColors.Authentication.IconColor}`,
    BorderColor: "red",
  },
  rightInputIcon: {
    ml: 1,
    color: `${DamsColors.Authentication.IconColor}`,
  },
};

export default AuthInput;
